import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

/** Mandantenspezifische Anforderungs-Kategorie (tenant-scoped, projectapi Migration 063). */
export interface AnforderungKategorie {
  id: number;
  name: string;
  sort: number;
  active: boolean;
}

const KEY = ['anforderung-kategorien'];

/** Lädt die Kategorien des Mandanten. Erster GET seedet serverseitig 5 Defaults
 *  (seed-once). Degradiert auf leer, falls der Endpoint (noch) nicht erreichbar ist. */
export function useAnforderungKategorien() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      try { return await apiClient.get('/api/v1/anforderung-kategorien'); }
      catch { return { success: false, data: [] }; }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useCreateAnforderungKategorie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; sort?: number; active?: boolean }) =>
      apiClient.post('/api/v1/anforderung-kategorien', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateAnforderungKategorie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Pick<AnforderungKategorie, 'name' | 'sort' | 'active'>> }) =>
      apiClient.patch(`/api/v1/anforderung-kategorien/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteAnforderungKategorie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/anforderung-kategorien/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Komfort-Hook: aktive Kategorie-Namen, nach sort sortiert — für die Dropdowns. */
export function useAnforderungKategorieNamen(): string[] {
  const { data } = useAnforderungKategorien();
  const rows = (data as unknown as { data?: AnforderungKategorie[] } | undefined)?.data ?? [];
  return rows
    .filter((k) => k.active)
    .sort((a, b) => a.sort - b.sort)
    .map((k) => k.name);
}
